import { IAccount } from "@modeso/types__aumico-lib-annualreport-be";
import { displayAccountNumber } from "../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassAccountNumber";

/**
 * A helper class to manage account name masking logic, including "Deep Rename" functionality.
 */
export class MaskingHelper {

    /** Generates the unique identifier for an account used in the maskedNamesMap.
    /* This identifier includes shareholder information if applicable.
    */
    public static getAccountIdentifier(accountNumber: string, originalName: string, shareholder?: string) {
        const isShareholderAccount = shareholder != null && shareholder !== '';
        const identifier = isShareholderAccount
            ? `${accountNumber}#${originalName} - ${shareholder}`
            : `${accountNumber}#${originalName}`;
        return identifier;
    }

    /**
     * Separator between the base identifier and the row-scoped discriminator (see
     * {@link getRowIdentifier}). Chosen so a base identifier is always a PREFIX of its row-scoped
     * form, which is what makes {@link baseIdentifierOf} a plain string cut.
     */
    public static readonly ROW_IDENTIFIER_MARKER = '#uid:';

    /**
     * The identifier of ONE ROW.
     *
     * `getAccountIdentifier` identifies an account by number + name, which is not unique: two
     * accounts may share BOTH (a "true duplicate"). Every per-row thing keyed on it — selection,
     * group membership, rename, sort position — therefore addressed both rows at once, so the user
     * could not, for example, put the two instances into different groups.
     *
     * A true duplicate carries a `uniqueAccountId` (assigned deterministically from its import row,
     * see `assignUniqueAccountIds`), and appending it separates the rows. Accounts that are NOT
     * duplicates have no `uniqueAccountId` and keep the plain 2-part identifier — so nothing changes
     * for them, in memory or in what gets saved.
     */
    public static getRowIdentifier(account: {
        accountNumber: string;
        name?: string | null;
        originalName?: string | null;
        shareholder?: string;
        uniqueAccountId?: string;
    }): string {
        const base = this.getAccountIdentifier(
            account.accountNumber,
            account.originalName ?? account.name ?? '',
            account.shareholder,
        );
        return account.uniqueAccountId ? `${base}${this.ROW_IDENTIFIER_MARKER}${account.uniqueAccountId}` : base;
    }

    /**
     * Every identifier form that may address `account` in PERSISTED grouping/masking state, most
     * specific first.
     *
     * The second entry is what keeps existing reports working: before row identity existed, the app
     * could only write the 2-part form, and for a duplicate pair that single entry stood for BOTH
     * instances. Reads must still honour it — otherwise opening an old report would show a group
     * having silently lost its members. A row-scoped entry, being more specific, wins where both
     * are present.
     */
    public static getIdentifierCandidates(account: {
        accountNumber: string;
        name?: string | null;
        originalName?: string | null;
        shareholder?: string;
        uniqueAccountId?: string;
    }): string[] {
        const base = this.getAccountIdentifier(
            account.accountNumber,
            account.originalName ?? account.name ?? '',
            account.shareholder,
        );
        return account.uniqueAccountId
            ? [`${base}${this.ROW_IDENTIFIER_MARKER}${account.uniqueAccountId}`, base]
            : [base];
    }

    /** The 2-part form of an identifier, whether or not it carries a row-scoped part. */
    public static baseIdentifierOf(identifier: string): string {
        const at = (identifier ?? '').indexOf(this.ROW_IDENTIFIER_MARKER);
        return at === -1 ? identifier : identifier.slice(0, at);
    }

    /** Whether `identifier` addresses one specific row rather than every account with that number + name. */
    public static isRowScopedIdentifier(identifier: string): boolean {
        return (identifier ?? '').includes(this.ROW_IDENTIFIER_MARKER);
    }

    /**
     * Generates the unique key for an account used in the maskedNamesMap.
     * This key changes depending on whether "Deep Rename" is active.
     *
     * @param account The account object.
     * @param parentSortingId The `sortingId` of the parent FLI/Sub-FLI, required for deep renaming.
     * @param deepRenameEnabled Flag indicating if deep renaming is active.
     * @returns The unique key for the masked name map.
     */
    public static getMaskingKey(
        account: IAccount,
        parentSortingId: string | undefined,
        deepRenameEnabled: boolean
    ): string {
        // Row-scoped for a true duplicate, plain 2-part for every other account — so renaming one
        // instance of a duplicate pair no longer renames the other.
        const identifier = this.getRowIdentifier(account);
        if (deepRenameEnabled && parentSortingId) {
            // Deep Rename Key: uses parent path to create a specific key
            return `${parentSortingId}__acc__${identifier}`;
        }
        // Standard Key: uses only the identifier
        return identifier;
    }

    /**
     * Every masking key that may hold a name for `account`, most specific first.
     *
     * Reads go through this rather than {@link getMaskingKey} so a rename SAVED before row identity
     * existed (2-part key, standing for every instance of a duplicate) still resolves.
     */
    public static getMaskingKeys(
        account: IAccount,
        parentSortingId: string | undefined,
        deepRenameEnabled: boolean
    ): string[] {
        const candidates = this.getIdentifierCandidates(account);
        if (deepRenameEnabled && parentSortingId) {
            return candidates.map((identifier) => `${parentSortingId}__acc__${identifier}`);
        }
        return candidates;
    }

    /** The masked name stored for `account` under any of its keys, or undefined. */
    public static resolveMaskedName(
        account: IAccount,
        parentSortingId: string | undefined,
        maskedNamesMap: { [key: string]: string },
        deepRenameEnabled: boolean
    ): string | undefined {
        if (!maskedNamesMap) return undefined;
        for (const key of this.getMaskingKeys(account, parentSortingId, deepRenameEnabled)) {
            if (maskedNamesMap[key]) return maskedNamesMap[key];
        }
        return undefined;
    }

    /**
     * Determines if an account has been masked.
     *
     * @param account The account object.
     * @param parentSortingId The `sortingId` of the parent FLI/Sub-FLI.
     * @param maskedNamesMap The map of masked names.
     * @param deepRenameEnabled Flag indicating if deep renaming is active.
     * @returns `true` if the account has a masked name, otherwise `false`.
     */
    public static isAccountMasked(
        account: IAccount,
        parentSortingId: string | undefined,
        maskedNamesMap: { [key: string]: string },
        deepRenameEnabled: boolean
    ): boolean {
        return this.resolveMaskedName(account, parentSortingId, maskedNamesMap, deepRenameEnabled) != null;
    }

    /**
     * Gets the display name for an account, returning the masked name if it exists,
     * otherwise falling back to the original account name.
     *
     * @param account The account object.
     * @param parentSortingId The `sortingId` of the parent FLI/Sub-FLI.
     * @param maskedNamesMap The map of masked names.
     * @param deepRenameEnabled Flag indicating if deep renaming is active.
     * @returns The masked name or the original account name.
     */
    public static getAccountDisplayName(
        account: IAccount,
        parentSortingId: string | undefined,
        maskedNamesMap: { [key: string]: string },
        deepRenameEnabled: boolean,
        addAccountNumber: boolean = false,
        applyGroupingMasking: boolean = false,
    ): string {
        if (applyGroupingMasking === false) {
            return addAccountNumber ? `${account.accountNumber} ${account.name}` : account.name;
        }
        const maskedName = this.resolveMaskedName(account, parentSortingId, maskedNamesMap, deepRenameEnabled);
        if (addAccountNumber) {
            // REV-21798: system-created reclassification accounts are shown as
            // `MAN`; their generated number is an internal identity, never
            // displayed. Applied before the length rule below so the mapping is
            // explicit rather than an accident of the number's length.
            let accountNumber = displayAccountNumber(account.accountNumber);
            // We assume it's an internal account number if it's longer than 20 characters
            if (accountNumber.length > 20) {
                accountNumber = '';
            }
            if (maskedName) {
                return `${accountNumber} ${maskedName}`;
            }
            return `${accountNumber} ${account.name}`;
        }
        return maskedName ?? account.name;
    }
}
